/**
 * TestDataCleanupPage — Admin utility (2026-02).
 *
 * One-click bulk purge for test / seed clients created by pytest
 * runners, browser automation, or manual QA. Every purge is:
 *   1. Preview first (dry-run count of clients + downstream data)
 *   2. Explicit "Yes, purge" confirmation
 *   3. Cascade hard-delete (client + properties + appraisals + …)
 *
 * Reuses `POST /api/admin/test-data/{preview,purge}`.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Trash2, AlertTriangle, Loader2, Search, ShieldAlert } from 'lucide-react';

export default function TestDataCleanupPage() {
  const { api } = useAuth();
  const [presets, setPresets] = useState([]);
  const [minLen, setMinLen] = useState(4);
  const [pattern, setPattern] = useState('PytestRunner');
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [purging, setPurging] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    api.get('/admin/test-data/presets').then(({ data }) => {
      setPresets(data?.presets || []);
      setMinLen(data?.min_pattern_length || 4);
    }).catch(() => {});
  }, [api]);

  const runPreview = useCallback(async () => {
    if ((pattern || '').trim().length < minLen) {
      toast.error(`Pattern must be at least ${minLen} characters`);
      return;
    }
    setPreviewing(true);
    setPreview(null);
    setLastResult(null);
    try {
      const { data } = await api.post('/admin/test-data/preview', { pattern });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  }, [api, pattern, minLen]);

  const runPurge = useCallback(async () => {
    if (!preview || preview.counts.clients === 0) return;
    const msg = `Cascade hard-delete ${preview.counts.clients} client${preview.counts.clients === 1 ? '' : 's'} matching "${pattern}"?\n\n` +
                `This ALSO deletes:\n` +
                `  • ${preview.counts.properties} propert${preview.counts.properties === 1 ? 'y' : 'ies'}\n` +
                `  • ${preview.counts.appraisals} appraisal${preview.counts.appraisals === 1 ? '' : 's'}\n` +
                `  • ${preview.counts.quote_items} quote_items\n` +
                `  • ${preview.counts.component_lines} component_lines\n` +
                `  • ${preview.counts.invoices} invoices\n\n` +
                `IRREVERSIBLE. Confirm?`;
    if (!window.confirm(msg)) return;
    setPurging(true);
    try {
      const { data } = await api.post('/admin/test-data/purge', { pattern });
      setLastResult(data);
      setPreview(null);
      toast.success(`Purged ${data.deleted} client${data.deleted === 1 ? '' : 's'}${data.failed ? ` · ${data.failed} failed` : ''}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Purge failed');
    } finally {
      setPurging(false);
    }
  }, [api, pattern, preview]);

  return (
    <div data-testid="test-data-cleanup-page" className="max-w-4xl mx-auto p-6 space-y-4">
      <div className="flex items-center gap-2 border-b border-slate-200 pb-3">
        <ShieldAlert size={20} className="text-rose-600" />
        <h1 className="text-xl font-bold text-slate-800 font-[Manrope]">Test Data Cleanup</h1>
      </div>

      <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-sm text-rose-900 flex gap-2">
        <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">Irreversible cascade hard-delete.</p>
          <p className="text-xs text-rose-800/80 mt-0.5">
            Every matching client AND their properties, appraisals, quote_items,
            component_lines and invoices are permanently removed. Anchored to
            client name / company only — never matches on address / email / phone.
          </p>
        </div>
      </div>

      {/* Preset buttons */}
      <div className="bg-white border border-slate-200 rounded-md p-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">One-click presets</div>
        <div className="flex flex-wrap gap-2">
          {presets.map((p) => (
            <button
              key={p.pattern}
              data-testid={`preset-${p.pattern}`}
              onClick={() => { setPattern(p.pattern); setPreview(null); setLastResult(null); }}
              className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                pattern === p.pattern
                  ? 'bg-rose-600 text-white border-rose-600'
                  : 'bg-white border-slate-300 text-slate-700 hover:bg-rose-50 hover:border-rose-300'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Pattern input */}
      <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2">
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Pattern (name / company substring, case-insensitive, min {minLen} chars)</span>
          <input
            type="text"
            data-testid="test-data-pattern-input"
            value={pattern}
            onChange={(e) => { setPattern(e.target.value); setPreview(null); }}
            placeholder="PytestRunner"
            className="mt-1 w-full text-sm px-3 py-2 border border-slate-300 rounded font-mono-num"
          />
        </label>
        <button
          data-testid="test-data-preview-btn"
          onClick={runPreview}
          disabled={previewing || (pattern || '').trim().length < minLen}
          className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-40"
        >
          {previewing ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          {previewing ? 'Counting…' : 'Preview matches'}
        </button>
      </div>

      {/* Preview result */}
      {preview && (
        <div data-testid="test-data-preview-result" className="bg-white border border-amber-300 rounded-md p-3 space-y-3">
          <div className="text-sm font-bold text-amber-900">
            Preview for pattern <code className="bg-amber-100 px-1 rounded">{preview.pattern}</code>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center text-xs">
            {Object.entries(preview.counts).map(([k, v]) => (
              <div key={k} className={`rounded border p-2 ${v > 0 ? 'bg-rose-50 border-rose-200' : 'bg-slate-50 border-slate-200'}`}>
                <div className="text-[10px] uppercase tracking-wide text-slate-500">{k}</div>
                <div className={`text-lg font-bold ${v > 0 ? 'text-rose-700' : 'text-slate-400'}`} data-testid={`preview-count-${k}`}>{v}</div>
              </div>
            ))}
          </div>
          {preview.sample_client_names?.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-slate-600 hover:text-slate-900">Sample matches ({preview.sample_client_names.length} of {preview.counts.clients} shown)</summary>
              <ul className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5 text-slate-700 font-mono-num text-[11px] pl-3">
                {preview.sample_client_names.map((n) => <li key={n}>· {n}</li>)}
              </ul>
            </details>
          )}
          <button
            data-testid="test-data-purge-btn"
            onClick={runPurge}
            disabled={purging || preview.counts.clients === 0}
            className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40"
          >
            {purging ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            {purging
              ? 'Purging…'
              : preview.counts.clients === 0
                ? 'No matches to delete'
                : `Purge ${preview.counts.clients} client${preview.counts.clients === 1 ? '' : 's'}`}
          </button>
        </div>
      )}

      {/* Result */}
      {lastResult && (
        <div data-testid="test-data-purge-result" className={`rounded-md p-3 border ${lastResult.failed ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-300'}`}>
          <div className="text-sm font-bold text-slate-800">{lastResult.message}</div>
          {lastResult.failed > 0 && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer text-amber-800">Failures ({lastResult.failed})</summary>
              <ul className="mt-1 text-amber-900 font-mono-num text-[11px] pl-3">
                {(lastResult.failures || []).map((f, i) => (
                  <li key={i}>· {f.client_id?.slice(-8)}: {f.detail}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
